import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'
import { Button, Flex, Input, Skeleton } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { MobileFilterSheet } from '@pushly/aqe/lib/components/mobile-filter-sheet/mobile-filter-sheet'
import { StatusBadge, SourceBadge } from '@pushly/aqe/lib/components/badges'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import PushlyIcon from '@pushly/aqe/lib/components/icons/pushly-icon'
import type { FilterDefinition, ActiveFilter } from '@pushly/aqe/lib/components/filter-bar/types'
import { useAppState } from '../../hooks/use-mobile-service'
import { useDebouncer } from '../../../hooks/use-debouncer'
import { useNotifications } from '../../../hooks/use-notifications'
import { useDrafts } from '../../hooks/use-drafts'
import { NotificationCard } from '../notification-card/notification-card'
import { NotificationListEmpty } from './notification-list-empty'
import { StatusType, HOLD_OUT_COMPUTED_STATUS } from '../../../enums/status-type'
import { NotificationSource } from '../../../enums/notification-source'
import { SegmentSource } from '@pushly/models/lib/enums'
import { DomainService } from '../../../services'
import { getSegmentFilterOptions } from '../../../_utils/segment-list'
import './notification-list-view.scss'

// --- Filter option constants ---

const STATUS_OPTIONS = [
    {
        value: StatusType.COMPLETED.name,
        label: <StatusBadge type="notification" status={StatusType.COMPLETED.name} expanded hideTooltip />,
    },
    {
        value: StatusType.SCHEDULED.name,
        label: <StatusBadge type="notification" status={StatusType.SCHEDULED.name} expanded hideTooltip />,
    },
    {
        value: StatusType.DELIVERING.name,
        label: <StatusBadge type="notification" status={StatusType.DELIVERING.name} expanded hideTooltip />,
    },
    {
        value: HOLD_OUT_COMPUTED_STATUS,
        label: <StatusBadge type="notification" status={StatusType.COMPLETED.name} holdOut expanded hideTooltip />,
    },
    {
        value: StatusType.CANCELLED.name,
        label: <StatusBadge type="notification" status={StatusType.CANCELLED.name} expanded hideTooltip />,
    },
    {
        value: StatusType.FAILED.name,
        label: <StatusBadge type="notification" status={StatusType.FAILED.name} expanded hideTooltip />,
    },
]

const SOURCE_OPTIONS = [
    {
        value: NotificationSource.MANUAL,
        label: <SourceBadge type="notification" source={NotificationSource.MANUAL} expanded hideTooltip />,
    },
    {
        value: NotificationSource.FEED,
        label: <SourceBadge type="notification" source={NotificationSource.FEED} expanded hideTooltip />,
    },
]

const DEFAULT_STATUSES = STATUS_OPTIONS.map((o) => o.value)
const DEFAULT_SOURCES = SOURCE_OPTIONS.map((o) => o.value)

// Module-level set: tracks which list modes have shown the swipe peek hint this session.
// Each mode (notifications, drafts) gets one peek per browser session.
// Resets on full page reload. Permanently disabled after first actual swipe (persisted in extras).
const swipeHintShownModes = new Set<string>()

// --- Component ---

export interface NotificationListViewProps {
    mode: 'notifications' | 'drafts'
    /** Exposes the active list's refresh function to the parent */
    onRefreshRef?: React.MutableRefObject<(() => void) | null>
}

export const NotificationListView: React.FC<NotificationListViewProps> = observer(({ mode, onRefreshRef }) => {
    const appState = useAppState()

    const domainId = appState.currentDomain?.id
    const userId = appState.currentUser?.id

    const isDrafts = mode === 'drafts'
    const cardVariant = isDrafts ? 'draft' : 'notification'
    const searchPlaceholder = isDrafts ? 'Search drafts...' : 'Search notifications...'

    // --- Search ---
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [runWithDebounce] = useDebouncer(350)

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchTerm(value)
            runWithDebounce(() => setDebouncedSearch(value))
        },
        [runWithDebounce],
    )

    // --- Filters (persisted for notifications, simple for drafts) ---
    const userPreferences = useUserPreferences(userId, domainId)

    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
        if (isDrafts) return []
        const saved = userPreferences.get(['notification_status_filters'], [])
        return saved.length > 0 ? saved : DEFAULT_STATUSES
    })
    const [selectedSources, setSelectedSources] = useState<string[]>(() => {
        const prefsKey = isDrafts ? 'draft_source_filters' : 'notification_source_filters'
        const saved = userPreferences.get([prefsKey], [])
        return saved.length > 0 ? saved : DEFAULT_SOURCES
    })
    const [selectedSegments, setSelectedSegments] = useState<Array<string | number>>(() => {
        const prefsKey = 'notification_segment_filters'
        const saved: Array<Record<string, string> | string | number> = userPreferences.get([prefsKey], [])
        if (saved.length > 0) {
            return saved.map((obj) => {
                if (typeof obj === 'object' && obj !== null) {
                    const [[key]] = Object.entries(obj)
                    return Number(key)
                }
                return obj as string | number
            })
        }
        return []
    })

    // Reset filters on domain change
    const prevDomainIdRef = useRef(domainId)
    useEffect(() => {
        if (prevDomainIdRef.current !== domainId) {
            prevDomainIdRef.current = domainId
            setSearchTerm('')
            setDebouncedSearch('')

            if (!isDrafts) {
                const savedStatuses = userPreferences.get(['notification_status_filters'], [])
                setSelectedStatuses(savedStatuses.length > 0 ? savedStatuses : DEFAULT_STATUSES)
            }

            const sourcePrefsKey = isDrafts ? 'draft_source_filters' : 'notification_source_filters'
            const savedSources = userPreferences.get([sourcePrefsKey], [])
            setSelectedSources(savedSources.length > 0 ? savedSources : DEFAULT_SOURCES)

            const prefsKey = 'notification_segment_filters'
            const savedSegments: Array<Record<string, string> | string | number> = userPreferences.get([prefsKey], [])
            if (savedSegments.length > 0) {
                setSelectedSegments(
                    savedSegments.map((obj) => {
                        if (typeof obj === 'object' && obj !== null) {
                            const [[key]] = Object.entries(obj)
                            return Number(key)
                        }
                        return obj as string | number
                    }),
                )
            } else {
                setSelectedSegments([])
            }
        }
    }, [domainId, userPreferences, isDrafts])

    // --- Data fetching: each mode manages its own hook ---
    const notifFilters = useMemo(
        () => ({
            statuses: selectedStatuses,
            sources: selectedSources,
            segments: selectedSegments,
            search: debouncedSearch || undefined,
        }),
        [selectedStatuses, selectedSources, selectedSegments, debouncedSearch],
    )

    const notifResult = useNotifications(isDrafts ? { statuses: [], enabled: false } : notifFilters)
    const draftsResult = useDrafts({
        enabled: isDrafts,
        sources: isDrafts ? selectedSources : undefined,
        segmentIds: isDrafts ? selectedSegments : undefined,
    })

    // Expose refresh to parent
    useEffect(() => {
        if (onRefreshRef) {
            onRefreshRef.current = isDrafts ? draftsResult.refresh : notifResult.refresh
        }
    }, [onRefreshRef, notifResult.refresh, draftsResult.refresh, isDrafts])

    // Resolve data source based on mode
    const data = isDrafts ? draftsResult.data : notifResult.data
    const loading = isDrafts ? draftsResult.loading : notifResult.loading
    const error = isDrafts ? draftsResult.error : notifResult.error
    const hasMore = isDrafts ? draftsResult.hasMore : notifResult.hasMore
    const handleRefresh = isDrafts ? draftsResult.refresh : notifResult.refresh
    const handleLoadMore = isDrafts ? draftsResult.loadMore : notifResult.loadMore
    const handleArchive = isDrafts ? draftsResult.archiveDraft : undefined

    // Build filter definitions based on mode
    const filterDefinitions: FilterDefinition[] = useMemo(() => {
        const segmentFilter: FilterDefinition = {
            key: 'segments',
            label: 'Segments',
            type: 'multi-select',
            showOperatorSelect: false,
            showSearch: true,
            loadOptions: async () => {
                if (!domainId) return []
                const domainService = Container.get(DomainService)
                const res = await domainService.fetchSegmentsByDomainId(domainId, {
                    query: {
                        includeTreated: 0,
                        pagination: 0,
                        fields: 'id,name,source,computedStatus,isDefault,iconUrl,flags,lastTargetedDateUtc,segmentType',
                        source: SegmentSource.STANDARD,
                    },
                })
                if (!res.ok) return []
                return getSegmentFilterOptions(res.data)
            },
            defaultOperator: 'in',
        }

        if (isDrafts) {
            return [
                {
                    key: 'source',
                    label: 'Source',
                    type: 'multi-select',
                    showOperatorSelect: false,
                    showSearch: false,
                    selectOptions: SOURCE_OPTIONS,
                    defaultOperator: 'in',
                },
                segmentFilter,
            ]
        }

        return [
            {
                key: 'status',
                label: 'Status',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: STATUS_OPTIONS,
                defaultOperator: 'in',
            },
            {
                key: 'source',
                label: 'Source',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: SOURCE_OPTIONS,
                defaultOperator: 'in',
            },
            segmentFilter,
        ]
    }, [domainId, isDrafts])

    const handleFilterApply = useCallback(
        (activeFilters: ActiveFilter[]) => {
            if (!isDrafts) {
                const statusFilter = activeFilters.find((f) => f.key === 'status')
                const newStatuses = (statusFilter?.value as string[]) ?? []
                if (newStatuses.length > 0) {
                    setSelectedStatuses(newStatuses)
                    userPreferences.setDebounced(['notification_status_filters'], newStatuses)
                } else {
                    setSelectedStatuses(DEFAULT_STATUSES)
                    userPreferences.setDebounced(['notification_status_filters'], [])
                }
            }

            const sourceFilter = activeFilters.find((f) => f.key === 'source')
            const sourcePrefsKey = isDrafts ? 'draft_source_filters' : 'notification_source_filters'
            const newSources = (sourceFilter?.value as string[]) ?? []
            if (newSources.length > 0) {
                setSelectedSources(newSources)
                userPreferences.setDebounced([sourcePrefsKey], newSources)
            } else {
                setSelectedSources(DEFAULT_SOURCES)
                userPreferences.setDebounced([sourcePrefsKey], [])
            }

            const segmentFilter = activeFilters.find((f) => f.key === 'segments')
            const prefsKey = 'notification_segment_filters'
            if (segmentFilter && Array.isArray(segmentFilter.value) && segmentFilter.value.length > 0) {
                setSelectedSegments(segmentFilter.value)
                const segmentPrefs = segmentFilter.value.map((id: string | number) => ({ [id]: String(id) }))
                userPreferences.setDebounced([prefsKey], segmentPrefs)
            } else {
                setSelectedSegments([])
                userPreferences.setDebounced([prefsKey], [])
            }
        },
        [userPreferences, isDrafts],
    )

    // Build active filters for the MobileFilterSheet
    const activeFilters = useMemo((): ActiveFilter[] => {
        const filters: ActiveFilter[] = []

        if (!isDrafts) {
            if (selectedStatuses.length > 0 && selectedStatuses.length < DEFAULT_STATUSES.length) {
                filters.push({ key: 'status', operator: 'in', value: selectedStatuses })
            }
        }

        if (selectedSources.length > 0 && selectedSources.length < DEFAULT_SOURCES.length) {
            filters.push({ key: 'source', operator: 'in', value: selectedSources })
        }

        if (selectedSegments.length > 0) {
            filters.push({ key: 'segments', operator: 'in', value: selectedSegments })
        }

        return filters
    }, [isDrafts, selectedStatuses, selectedSources, selectedSegments])

    // --- Filtered data (client-side search for drafts, server-side for notifications) ---
    const displayData = useMemo(() => {
        if (!isDrafts) return data // notifications use server-side search
        if (!debouncedSearch.trim()) return data
        const term = debouncedSearch.toLowerCase()
        return data.filter((d) => d.title.toLowerCase().includes(term) || d.body?.toLowerCase().includes(term))
    }, [data, debouncedSearch, isDrafts])

    const hasActiveFilters = debouncedSearch.trim().length > 0 || activeFilters.length > 0

    // Swipe hint: peek animation on first card.
    // - Shows once per list mode per session (notifications gets one, drafts gets one)
    // - Permanently disabled after user performs their first swipe (persisted in extras)
    const SWIPE_HINT_PREF_KEY = 'mobile_swipe_hint_shown'
    const swipeHintPermanentlyDismissed = userPreferences.get(['extras', SWIPE_HINT_PREF_KEY], false)

    const showHint = !swipeHintPermanentlyDismissed && !swipeHintShownModes.has(mode)
    // Mark this mode as shown after first render with data
    if (showHint && displayData.length > 0 && !loading) {
        swipeHintShownModes.add(mode)
    }

    const handleSwipeHintDismiss = useCallback(() => {
        userPreferences.setUnscoped(['extras', SWIPE_HINT_PREF_KEY], true)
    }, [userPreferences])

    // --- Render ---

    if (error && data.length === 0 && !loading && !appState.isDomainSwitching) {
        return <NotificationListEmpty variant="error" isDrafts={isDrafts} onRetry={handleRefresh} />
    }

    return (
        <div className="notification-list-view">
            <Flex gap="var(--space-2)" align="center" className="notification-list-view__filter">
                <Input
                    placeholder={searchPlaceholder}
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    allowClear
                    size="middle"
                    className="notification-list-view__search"
                />
                <MobileFilterSheet
                    key={domainId}
                    filterDefinitions={filterDefinitions}
                    value={activeFilters}
                    onApply={handleFilterApply}
                />
            </Flex>

            {(loading || appState.isDomainSwitching) && data.length === 0 ? (
                <Flex vertical gap="var(--space-2)" className="notification-list-view__skeleton">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="notification-list-view__skeleton-card">
                            <Skeleton active paragraph={{ rows: 2 }} />
                        </div>
                    ))}
                </Flex>
            ) : displayData.length === 0 ? (
                <NotificationListEmpty variant={hasActiveFilters ? 'filtered' : 'no-data'} isDrafts={isDrafts} />
            ) : (
                <div className="notification-list-view__items-container">
                    {loading && data.length > 0 && (
                        <div className="notification-list-view__loading-overlay" aria-live="polite">
                            <span className="notification-list-view__loading-icon">
                                <PushlyIcon aria-hidden="true" />
                            </span>
                        </div>
                    )}

                    <Flex vertical gap="var(--space-2)" className="notification-list-view__items" role="list">
                        {displayData.map((item, index) => (
                            <NotificationCard
                                key={item.id}
                                notification={item}
                                variant={cardVariant}
                                onChange={handleRefresh}
                                onArchive={handleArchive}
                                hintSwipe={index === 0 && showHint}
                                onSwipeReveal={handleSwipeHintDismiss}
                            />
                        ))}
                    </Flex>

                    {hasMore && (
                        <Flex justify="center" className="notification-list-view__load-more">
                            <Button size="middle" onClick={handleLoadMore} loading={loading}>
                                Load more
                            </Button>
                        </Flex>
                    )}
                </div>
            )}
        </div>
    )
})

NotificationListView.displayName = 'NotificationListView'
